import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import tokenManager from '../utils/tokenManager';
import './SharedClipboard.css';

export default function SharedClipboard() {
  const [content, setContent] = useState('');
  const [draft, setDraft] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const savedContent = useRef('');

  useEffect(() => {
    let active = true;
    let socket;
    let reconnect;
    const apply = (data) => {
      if (!active) return;
      const previous = savedContent.current;
      savedContent.current = data.content;
      setContent(data.content);
      setDraft((current) => current === previous ? data.content : current);
      setUpdatedAt(data.updated_at);
    };
    api.get('/clipboard').then(({ data }) => {
      if (active) {
        savedContent.current = data.content;
        setContent(data.content);
        setDraft(data.content);
        setUpdatedAt(data.updated_at);
      }
    }).catch(() => toast.error('Could not load clipboard'));
    const connect = () => {
      if (!active) return;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(`${protocol}//${window.location.host}/api/clipboard/live`);
      socket.onopen = () => socket.send(JSON.stringify({ token: tokenManager.getToken() }));
      socket.onmessage = (event) => apply(JSON.parse(event.data));
      socket.onclose = () => { if (active) reconnect = window.setTimeout(connect, 3000); };
    };
    connect();
    return () => {
      active = false;
      window.clearTimeout(reconnect);
      if (socket) socket.close();
    };
  }, []); // The socket owns updates after the initial load.

  const save = async (value = draft) => {
    setSaving(true);
    try {
      const { data } = await api.put('/clipboard', { content: value });
      savedContent.current = data.content;
      setContent(data.content);
      setDraft(data.content);
      setUpdatedAt(data.updated_at);
      toast.success(value ? 'Saved to shared clipboard' : 'Clipboard cleared');
    } catch (error) {
      toast.error('Could not save clipboard');
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied');
    } catch (error) {
      toast.error('Could not copy. Select the text and copy it manually.');
    }
  };

  return <section className="shared-clipboard">
    <h2>Shared clipboard</h2>
    <p>Paste text here on one device, then open this page on another device with the same account.</p>
    <label htmlFor="shared-clipboard-text">Text</label>
    <textarea id="shared-clipboard-text" value={draft} onChange={(event) => setDraft(event.target.value)} maxLength={10000} placeholder="Paste a link, note, or other text…" rows={10} />
    <div className="shared-clipboard-actions">
      <button type="button" onClick={() => save()} disabled={saving || draft === content}>Save</button>
      <button type="button" onClick={copy} disabled={!content}>Copy</button>
      <button type="button" onClick={() => save('')} disabled={saving || (!content && !draft)}>Clear</button>
    </div>
    {updatedAt && <small>Last saved {new Date(updatedAt).toLocaleString()}</small>}
  </section>;
}
